import { CKButton } from '@clearkit/react';
import FormLabel from '@material-ui/core/FormLabel';
import Warning from '@material-ui/icons/Warning';
import ToggleButton from '@material-ui/lab/ToggleButton';
import { Link } from 'react-router-dom';
import { Subscribe } from 'unstated';

import Constants from '~/components/Constants';
import LoadingArea from '~/components/LoadingArea';
import { SidebarDivider, SidebarFooter } from '~/components/Sidebar';
import Switch from '~/components/SwitchWithLabel';
import TextField from '~/components/TextField';
import ToggleRadioButtonGroup from '~/components/ToggleRadioButtonGroup';
import ConnectorsContainer from '~/containers/Connectors';
import UserContainer from '~/containers/User';
import { getSalesforceObjectFields } from '~/lib/client';
import NotificationBar from '~/pages/forms/components/NotificationBar';

import ContactForm from '../ContactForm';
import styles from '../SharedSettings.style';
import Sidebar, {
  SidebarHeader,
  SidebarTitle,
  SidebarTypography,
} from '../Sidebar';
import FieldMapping from './FieldMapping';

const ConnectorInfo = ({ connector }) => {
  return (
    <SidebarTypography>
      You&apos;re connected to{' '}
      <span style={{ fontWeight: 500 }}>{connector.name}</span>. You can connect
      to a different Salesforce account by visiting the{' '}
      <Link to="/settings/configuration/salesforce">Configuration page</Link>.
    </SidebarTypography>
  );
};

const NoConnectorInfo = ({ isEnabled }) => (
  <>
    <SidebarTypography>
      To sync form responses to Salesforce, first you will need to{' '}
      <Link to="/settings/configuration/salesforce">
        Connect to Salesforce.
      </Link>{' '}
    </SidebarTypography>
    {isEnabled && (
      <SidebarTypography>
        If you previously set up this form using credentials, those credentials
        will continue to work until you connect your Salesforce Account in your
        account settings.
      </SidebarTypography>
    )}
  </>
);

class SalesforceSettings extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      form: this.props.form,
      salesforceObjectFields: {},
      connectionStatus: null,
      loading: false,
      formDirty: false,
      invalid: false,
    };
  }

  componentDidMount() {
    const { form } = this.state;

    this.setState({ loading: true });

    const objectType = form.salesforce_object || 'lead';

    getSalesforceObjectFields(form.id, objectType)
      .then((salesforceObjectFields) =>
        this.setState({ salesforceObjectFields, loading: false }),
      )
      .catch(() => this.setState({ loading: false }));
  }

  hasConnector = () => {
    const connector = this.getConnector();

    return connector && Object.entries(connector).length > 0;
  };

  getConnector = () => {
    const connector = Object.entries(
      this.props.connectorsContainer.state.connectors,
    ).find((serviceConnector) => {
      const [service, _] = serviceConnector;
      return ['salesforce', 'salesforcesandbox'].includes(service);
    });
    return connector && connector[1];
  };

  setConnectionStatus = (connectionStatus) => {
    this.setState({ connectionStatus });
  };

  onChange = (attr, event) => {
    let form = { ...this.state.form };
    form[attr] = event.target.value;

    this.setState({ form, formDirty: true });
  };

  handleObjectChange = (attr, object_type) => {
    let form = { ...this.state.form };
    form[attr] = object_type;

    this.setState({ form, loading: true, formDirty: true }, () => {
      return getSalesforceObjectFields(form.id, object_type).then(
        (salesforceObjectFields) =>
          this.setState({ salesforceObjectFields, loading: false }),
      );
    });
  };

  saveForm = (form) => {
    if (this.hasConnector()) {
      return this.props
        .onUpdateForm(form)
        .then(() => this.setState({ formDirty: false }));
    }
  };

  discardChanges = () => {
    const { form: persistedForm } = this.props;

    const requestForSFDCMapping =
      persistedForm.salesforce_object !== this.state.form.salesforce_object;

    this.setState({ form: this.props.form, formDirty: false }, () => {
      const mapping = this.getSalesforceMapping();

      this.validateMapping(mapping);

      if (requestForSFDCMapping) {
        this.setState({ loading: true });

        getSalesforceObjectFields(
          persistedForm.id,
          persistedForm.salesforceObjectType || 'lead',
        ).then((salesforceObjectFields) =>
          this.setState({
            salesforceObjectFields,
            loading: false,
          }),
        );
      }
    });
  };

  getSalesforceMapping = () => {
    const { form } = this.state;

    switch (form.salesforce_object) {
      case 'contact':
        return form.salesforce_contact_mapping;
      default:
        return form.salesforce_lead_mapping;
    }
  };

  onToggle = () => {
    this.setState(
      (currentState) => ({
        form: {
          ...currentState.form,
          salesforce_enabled: !currentState.form.salesforce_enabled,
        },
      }),
      () =>
        this.props.onUpdateForm(
          {
            ...this.props.form,
            salesforce_enabled: this.state.form.salesforce_enabled,
          },
          `Salesforce ${
            this.state.form.salesforce_enabled ? 'enabled' : 'disabled'
          }`,
        ),
    );
  };

  scrollIntoView = (className) => {
    const component = document.querySelector(`.${className}`);

    if (!component) return;

    component.scrollIntoView({
      behavior: 'smooth',
    });
  };

  mappingHasDuplicateValues = (mapping) => {
    const values = Object.values(mapping);
    return new Set(values).size !== values.length;
  };

  validateMapping = (mapping) => {
    const { classes } = this.props;
    const invalid = this.mappingHasDuplicateValues(mapping);

    this.setState({ invalid }, () => {
      if (invalid) this.scrollIntoView(classes.notificationBar);
    });
  };

  changeMapping = (slug, value) => {
    if (!value || value === '') {
      this.deleteMapping(slug);
    } else {
      const { form: oldForm } = this.state;
      const newMapping = {
        ...this.getSalesforceMapping(),
        [slug]: value,
      };
      const newForm = {
        ...oldForm,
        [`salesforce_${oldForm.salesforce_object ||
          'lead'}_mapping`]: newMapping,
      };

      this.setState({ form: newForm, formDirty: true }, () =>
        this.validateMapping(newMapping),
      );
    }
  };

  deleteMapping = (slug) => {
    const { form: oldForm } = this.state;
    const { [slug]: _, ...newMapping } = this.getSalesforceMapping();
    const newForm = {
      ...oldForm,
      [`salesforce_${oldForm.salesforce_object || 'lead'}_mapping`]: newMapping,
    };

    this.setState({ form: newForm, formDirty: true });
  };

  render() {
    const { classes, userContainer } = this.props;
    const {
      form,
      salesforceObjectFields,
      loading,
      formDirty,
      invalid,
    } = this.state;

    return (
      <div className={classes.root}>
        {userContainer.shouldShowFormConnectors() ? (
          <>
            <Sidebar>
              <SidebarHeader>
                <SidebarTitle>Salesforce settings</SidebarTitle>
                <Switch
                  label="Enabled"
                  disabled={invalid}
                  checked={form.salesforce_enabled}
                  onChange={this.onToggle}
                />
              </SidebarHeader>

              {this.hasConnector() ? (
                <>
                  <ConnectorInfo connector={this.getConnector()} />
                  <div className={classes.input}>
                    <FormLabel>Salesforce Object Creation</FormLabel>
                    <ToggleRadioButtonGroup
                      exclusive
                      value={form.salesforce_object || 'lead'}
                      selected
                      className={classes.objectSwitch}
                      onChange={(_, object) =>
                        this.handleObjectChange('salesforce_object', object)
                      }>
                      <ToggleButton disabled={invalid} value="lead">
                        Lead
                      </ToggleButton>
                      <ToggleButton disabled={invalid} value="contact">
                        Contact
                      </ToggleButton>
                    </ToggleRadioButtonGroup>
                  </div>

                  <div className={classes.input}>
                    <TextField
                      fullWidth
                      autoComplete="salesforce-lead-source"
                      name="Salesforce Lead Source"
                      label="Salesforce Lead Source"
                      type="text"
                      value={form.salesforce_source || ''}
                      onChange={(e) => this.onChange('salesforce_source', e)}
                    />
                  </div>

                  <LoadingArea
                    className={classes.loadingArea}
                    loading={loading}>
                    <SidebarDivider className={classes.divider} />

                    <h2 className={classes.subtitle}>Custom mapping</h2>

                    <SidebarTypography>
                      Use the dropdown below to map any of your form field
                      responses to it&apos;s respective Lead or Contact
                    </SidebarTypography>

                    {Object.entries(salesforceObjectFields).length > 1 ? (
                      <FieldMapping
                        classes={classes}
                        fields={form.fields}
                        mapping={this.getSalesforceMapping()}
                        changeMapping={this.changeMapping}
                        salesforceObjectFields={salesforceObjectFields}
                      />
                    ) : (
                      <NotificationBar
                        className={classes.notificationBar}
                        adornment={<Warning className={classes.warningIcon} />}
                        content={{
                          message:
                            'Your Salesforce connection did not return any custom fields',
                          status: 'warn',
                        }}
                      />
                    )}
                  </LoadingArea>

                  {invalid && (
                    <NotificationBar
                      className={classes.notificationBar}
                      adornment={<Warning className={classes.warningIcon} />}
                      content={{
                        message: 'You cannot have duplicate field mappings',
                        status: 'warn',
                      }}
                    />
                  )}

                  <SidebarFooter className={classes.footer}>
                    <CKButton
                      style={{ marginRight: 10 }}
                      type="submit"
                      variant="bold"
                      variantColor="blue"
                      disabled={loading || invalid || !formDirty}
                      onClick={() => this.saveForm(form)}>
                      Save
                    </CKButton>

                    {formDirty && (
                      <CKButton
                        variant="simple"
                        variantColor="red"
                        onClick={this.discardChanges}>
                        Discard Changes
                      </CKButton>
                    )}
                  </SidebarFooter>
                </>
              ) : (
                <NoConnectorInfo isEnabled={form.salesforce_enabled} />
              )}
            </Sidebar>
          </>
        ) : (
          <ContactForm
            title="Salesforce settings"
            body="Access to premium integrations is available on Enterprise plans. Please contact our success team to get started!"
            form_id={Constants.CONTACT_SALES_FORM_ID}
          />
        )}
      </div>
    );
  }
}

SalesforceSettings.propTypes = {
  form: PropTypes.object.isRequired,
  classes: PropTypes.object.isRequired,
  onUpdateForm: PropTypes.func.isRequired,
  userContainer: PropTypes.object.isRequired,
  connectorsContainer: PropTypes.object.isRequired,
};

const SubscribedSalesforceSettings = ({ ...props }) => {
  return (
    <Subscribe to={[ConnectorsContainer, UserContainer]}>
      {(connectorsContainer, userContainer) => (
        <SalesforceSettings
          userContainer={userContainer}
          connectorsContainer={connectorsContainer}
          {...props}
        />
      )}
    </Subscribe>
  );
};

export default withStyles(styles)(SubscribedSalesforceSettings);
